import React, { useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  TextFields as InitialsIcon,
  Language as Language,
  Add,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/Index";
import { CompanyFormData, LogoMode } from "../../types/company-types";
import { FieldLabel, StyledTextField } from "../../types/company-form";
import { normalizeUrl } from "../../../../utils/normalizeUrl";

interface Step4BrandingProps {
  formData: CompanyFormData;
  setFormData: React.Dispatch<React.SetStateAction<CompanyFormData>>;
}

function getInitials(name: string) {
  if (!name.trim()) return "?";
  return name
    .trim()
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function nameGradient(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${h1},60%,55%), hsl(${h2},70%,45%))`;
}

const SUGGESTED_TAGS = [
  "Funeral Services",
  "Conferencing",
  "Supplier",
  "Partner",
  "Sponsor",
  "Media",
  "Catering",
  "Transport",
  "Flowers",
];

export default function Branding({
  formData,
  setFormData,
}: Step4BrandingProps) {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const color400 = reduxTheme.primary_colors?.[400] ?? "#8B4513";
  const color100 = reduxTheme.primary_colors?.[100];
  const color300 = reduxTheme.primary_colors?.[300];
  const color700 = reduxTheme.primary_colors?.[700];
  const color800 = reduxTheme.primary_colors?.[800];
  const color900 = reduxTheme.primary_colors?.[900];

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tagInput, setTagInput] = useState("");
  const [urlLoadFailed, setUrlLoadFailed] = useState(false);

  const handleLogoUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlLoadFailed(false);
    set("logoUrl")(e);
  };
  const set =
    (field: keyof CompanyFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleLogoModeChange = (_: React.MouseEvent, mode: LogoMode | null) => {
    if (!mode) return;
    setFormData((prev) => ({ ...prev, logoMode: mode }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, logoFile: file, logoMode: "upload" }));
  };

  const logoPreviewSrc = (() => {
    if (formData.logoMode === "upload" && formData.logoFile) {
      return URL.createObjectURL(formData.logoFile);
    }
    if (formData.logoMode === "url" && formData.logoUrl)
      return formData.logoUrl;
    return undefined;
  })();

  const addTag = (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || formData.tags.includes(trimmed)) return;
    setFormData((prev) => ({ ...prev, tags: [...prev.tags, trimmed] }));
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* ── Logo section ── */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography fontSize={16} fontWeight={700}>
          Logo
        </Typography>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
          {/* Logo preview box */}
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: "18px",
              overflow: "hidden",
              flexShrink: 0,
              background: !logoPreviewSrc
                ? nameGradient(formData.name)
                : undefined,
              boxShadow: "0 2px 12px rgba(0,0,0,0.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 22,
              fontWeight: 700,
              color: "#fff",
            }}
          >
            {formData.logoMode === "url" && formData.logoUrl ? (
              urlLoadFailed ? (
                <Typography
                  variant="caption"
                  sx={{ px: 1, textAlign: "center" }}
                >
                  Could not load image
                </Typography>
              ) : (
                <img
                  src={formData.logoUrl}
                  alt="Logo preview"
                  onError={() => setUrlLoadFailed(true)}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              )
            ) : logoPreviewSrc ? (
              <img
                src={logoPreviewSrc}
                alt="Logo preview"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              getInitials(formData.name)
            )}
          </Box>

          <Box sx={{ flex: 1 }}>
            {/* Mode toggle */}
            <ToggleButtonGroup
              value={formData.logoMode}
              exclusive
              onChange={handleLogoModeChange}
              size="small"
              sx={{
                mb: 0.5,
                mt: 3,
                bgcolor: color800,
                borderRadius: "12px",
                p: 0.5,
                "& .MuiToggleButtonGroup-grouped": {
                  border: "none",
                  borderRadius: "12px !important",
                  mx: 0.25,
                },
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontSize: 12,
                  px: 1.5,
                  py: 0.5,
                  color: color700,
                  "&.Mui-selected": {
                    bgcolor: color900,
                    color: color700,
                    "&:hover": { bgcolor: color900 },
                  },
                  "&:hover": { bgcolor: "transparent" },
                },
              }}
            >
              <ToggleButton value="initials">
                <InitialsIcon sx={{ fontSize: 14, mr: 0.5 }} /> Initials
              </ToggleButton>
              <ToggleButton value="url">
                <LinkIcon sx={{ fontSize: 14, mr: 0.5 }} /> URL
              </ToggleButton>
              <ToggleButton value="upload">
                <UploadIcon sx={{ fontSize: 14, mr: 0.5 }} /> Upload
              </ToggleButton>
            </ToggleButtonGroup>

            {formData.logoMode === "url" && (
              <StyledTextField
                placeholder="https://company.com/logo.png"
                value={formData.logoUrl}
                onChange={handleLogoUrlChange}
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
                sx={{ mt: 1.5 }}
              />
            )}

            {formData.logoMode === "upload" && (
              <Box sx={{ mt: 1.5 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileChange}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon sx={{ fontSize: 15 }} />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    fontSize: 12,
                    borderColor: color400,
                    color: color400,
                  }}
                >
                  {formData.logoFile ? formData.logoFile.name : "Choose file"}
                </Button>
                {formData.logoFile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {(formData.logoFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                )}
              </Box>
            )}

            {/* Auto-generated text — always below toggle */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {formData.logoMode === "initials"
                ? "Auto-generated from the company name."
                : formData.logoMode === "url"
                  ? "Paste a direct image URL."
                  : "Upload an image file for the logo."}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography fontSize={16} fontWeight={700}>
          Social Profiles
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {[
            {
              field: "linkedin" as const,
              icon: <LinkedInIcon sx={{ fontSize: 18, color: "#0077B5" }} />,
              placeholder: "https://linkedin.com/company/...",
              label: "LinkedIn",
            },
            {
              field: "youtube" as const,
              icon: <YouTubeIcon sx={{ fontSize: 18, color: "#FF0000" }} />,
              placeholder: "https://youtube.com/@company",
              label: "YouTube",
            },
            {
              field: "websiteBranding" as const,
              icon: <Language sx={{ fontSize: 18, color: "#6A1B9A" }} />,
              placeholder: "https://www.company.com",
              label: "Website",
            },
            {
              field: "facebook" as const,
              icon: <FacebookIcon sx={{ fontSize: 18, color: "#1877F2" }} />,
              placeholder: "https://facebook.com/...",
              label: "Facebook",
            },
            {
              field: "instagram" as const,
              icon: <InstagramIcon sx={{ fontSize: 18, color: "#E4405F" }} />,
              placeholder: "https://instagram.com/...",
              label: "Instagram",
            },
            {
              field: "x" as const,
              icon: <XIcon sx={{ fontSize: 18, color: "#000" }} />,
              placeholder: "https://x.com/...",
              label: "X (Twitter)",
            },
          ].map(({ field, icon, placeholder, label }) => (
            <Box key={field}>
              <FieldLabel textcolor={color100}>{label}</FieldLabel>
              <StyledTextField
                placeholder={placeholder}
                value={formData[field] as string}
                onChange={set(field)}
                onBlur={() =>
                  setFormData((prev) => ({
                    ...prev,
                    [field]: normalizeUrl(prev[field] as string),
                  }))
                }
                inputbg={color800}
                textcolor={color700}
                placeholdercolor={color300}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">{icon}</InputAdornment>
                  ),
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography fontWeight={700} fontSize={16}>
          Tags
        </Typography>

        {/* Suggested tags — selected ones highlighted, unselected show + */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {SUGGESTED_TAGS.map((tag) => {
            const isSelected = formData.tags.includes(tag);
            return (
              <Chip
                key={tag}
                label={tag}
                size="small"
                onClick={() => (isSelected ? removeTag(tag) : addTag(tag))}
                onDelete={isSelected ? () => removeTag(tag) : undefined}
                icon={isSelected ? undefined : <Add sx={{ fontSize: 14 }} />}
                sx={{
                  fontSize: 11,
                  cursor: "pointer",
                  bgcolor: isSelected ? `${color400}18` : "transparent",
                  color: isSelected ? color400 : "text.secondary",
                  border: "1px solid",
                  borderColor: isSelected ? color400 : "divider",
                  "& .MuiChip-deleteIcon": { color: color400 },
                  "& .MuiChip-icon": { color: "text.secondary" },
                }}
              />
            );
          })}
        </Box>

        {/* Custom tag input */}
        <StyledTextField
          placeholder="Type a tag and press Enter"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addTag(tagInput);
            }
          }}
          inputbg={color800}
          textcolor={color700}
          placeholdercolor={color300}
        />

        {/* Custom tags (not in SUGGESTED_TAGS) shown separately */}
        {formData.tags.filter((t) => !SUGGESTED_TAGS.includes(t)).length >
          0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {formData.tags
              .filter((t) => !SUGGESTED_TAGS.includes(t))
              .map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  onDelete={() => removeTag(tag)}
                  sx={{
                    fontSize: 11,
                    bgcolor: `${color400}18`,
                    color: color400,
                    border: "1px solid",
                    borderColor: color400,
                    "& .MuiChip-deleteIcon": { color: color400 },
                  }}
                />
              ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
